<template>
  <div class="sf-pad-2">
    <div class="sf-subnav">
      <div class="sf-subnav-inner sf-level sf-level-center" role="tablist">
        <SfTabItem
          id="permanent-documents-tab"
          :active="activeTab === 'categories'"
          @click="categoriesClick"
        >
          {{
            I18n.t(
              'components.document.existing_docs_modal.permanent_documents'
            )
          }}
        </SfTabItem>
        <SfTabItem
          id="period-documents"
          :active="activeTab === 'documents'"
          @click="documentsClick"
        >
          {{
            I18n.t(
              'components.document.existing_docs_modal.period_specific_documents'
            )
          }}
        </SfTabItem>
        <SfTabItem
          v-if="showExports"
          id="period-exports"
          :active="activeTab === 'exports'"
          @click="exportsClick"
        >
          {{ I18n.t('components.document.existing_docs_modal.exports') }}
        </SfTabItem>
      </div>
    </div>

    <div v-if="!loading" class="sf-pad-t-1">
      <div class="sf-grid sf-bg-snow sf-pad-2">
        <div v-if="activeTab !== 'exports'" class="sf-row">
          <div class="sf-col">
            <div class="sf-tab sf-level">
              <div class="tw-w-full">
                <SfSearchInput
                  v-model="searchQuery"
                  :placeholder="
                    I18n.t(
                      'components.document.existing_docs_modal.search_placeholder'
                    )
                  "
                  :show-clear-icon="false"
                  :show-result-navigation="false"
                />
              </div>
            </div>
          </div>
        </div>
        <ExistingDocumentsTable
          v-if="activeTab !== 'exports'"
          :entries="entries"
          :selected-entries-ids="selectedEntriesIds"
          :permanent-category="permanentCategory"
          @navigate="navigateTo"
          @toggle-selection="toggleSelection"
        />
        <ExportsTable
          v-else
          :entries="entries"
          :selected-entries-ids="selectedEntriesIds"
          @toggle-selection="toggleSelection"
        />
        <SfPagination
          v-if="activeTab === 'categories' && totalPages && totalPages > 1"
          :current-page="page"
          :total-pages="totalPages"
          @change-page="changePage"
        />
      </div>

      <div class="sf-pad-t-2">
        <div class="sf-level sf-level-center">
          <div>
            <a href="#" class="sf-btn" @click.prevent="back">
              {{ I18n.t('components.document.existing_docs_modal.back') }}
            </a>
          </div>
          <div>
            <a
              href="#"
              :data-heap="attachHeapId"
              data-testid="existing-attach-button"
              class="sf-btn sf-btn-primary"
              :class="{ 'sf-disabled': selectedEntriesIds.length === 0 }"
              @click.prevent="attach"
            >
              {{ successBtnTitle }}
            </a>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="loading" class="sf-level sf-level-around sf-pad-3">
      <i class="fa fa-spinner fa-spin export-spinner" />
    </div>
  </div>
</template>

<script>
import SFetch from '@app/utils/sfetch'
import ExistingDocumentsTable from '@app/application/reconciliation/sections/existing-documents-table'
import useI18n from '@app/shared/vue/composables/use-I18n'
import SfPagination from '@app/shared/vue/components/sf-pagination/sf-pagination.vue'
import ExportsTable from '@app/application/reconciliation/sections/exports-table.vue'
import SfSearchInput from '@app/shared/vue/components/sf-search/search-input.vue'
import SfTabItem from '@app/shared/vue/components/sf-tab-item.vue'
import { SfLog } from '@app/adapters/logger'

export default {
  name: 'SelectExistingDocument',

  components: {
    SfTabItem,
    SfSearchInput,
    ExistingDocumentsTable,
    ExportsTable,
    SfPagination,
  },

  props: {
    replacement: {
      type: Boolean,
      default: false,
    },
    showExports: {
      type: Boolean,
      required: false,
      default: true,
    },
  },

  emits: ['attach', 'back'],

  setup() {
    const I18n = useI18n()

    return {
      I18n,
    }
  },

  data() {
    return {
      activeTab: 'categories',
      documents: [],
      exports: [],
      permanentEntries: [],
      permanentCategory: null,
      category: null,
      entries: [],
      selectedEntries: [],
      searchQuery: '',
      page: 1,
      totalPages: 1,
      loading: false,
    }
  },

  computed: {
    selectedEntriesIds() {
      return this.selectedEntries.map((e) => e.id)
    },
    successBtnTitle() {
      return this.replacement
        ? I18n.t('components.document.existing_docs_page.replace')
        : I18n.t('components.document.existing_docs_modal.attach')
    },
    attachHeapId() {
      const prefix = 'existing-documents-attach-'
      switch (this.activeTab) {
        case 'categories':
          return `${prefix}permanent-documents`
        case 'documents':
          return `${prefix}period-specific`
        case 'exports':
          return `${prefix}exports`
        default:
          return ''
      }
    },
  },

  watch: {
    searchQuery: function () {
      this.search()
    },
  },

  created() {
    this.loading = true
    this.fetchCategories()
    this.fetchDocuments()
    if (this.showExports) {
      this.fetchExports()
    }
  },

  methods: {
    back() {
      this.$emit('back')
    },
    attach() {
      this.$emit('attach', this.selectedEntries)
    },
    changePage(page) {
      this.page = page
      this.fetchCategories(
        this.permanentCategory
          ? { permanent_category_id: this.permanentCategory.id }
          : {}
      )
    },
    categoriesClick() {
      this.activeTab = 'categories'
      this.updateEntries()
    },
    documentsClick() {
      this.activeTab = 'documents'
      this.updateEntries()
    },
    exportsClick() {
      this.activeTab = 'exports'
      this.updateEntries()
    },
    updateEntries() {
      switch (this.activeTab) {
        case 'documents':
          this.entries = this.documents
          this.category = null
          break
        case 'categories':
          this.entries = this.permanentEntries
          this.category = this.permanentCategory
          break
        case 'exports':
          this.entries = this.exports
          this.category = null
          break
        default:
          break
      }
    },
    toggleSelection(entry) {
      if (this.selectedEntries.some((e) => e.id === entry.id)) {
        this.selectedEntries = this.selectedEntries.filter(
          (e) => e.id !== entry.id
        )
      } else {
        if (this.replacement && this.selectedEntries.length === 1) return

        this.selectedEntries.push(entry)
      }
    },
    navigateTo(category) {
      this.loading = true
      this.page = 1
      this.fetchCategories({ permanent_category_id: category.id })
    },
    search() {
      const params = this.searchQuery ? { q: this.searchQuery } : {}

      switch (this.activeTab) {
        case 'documents':
          this.fetchDocuments(params)
          break
        case 'categories':
          this.fetchCategories(params)
          break
        case 'exports':
          this.fetchExports(params)
          break
        default:
          break
      }
    },
    fetchCategories(params = {}) {
      const url = Silverfin.Routes.select_from_documents_permanent_entries_path(
        {
          firm_id: Silverfin.currentFirmId,
          company_id: Silverfin.currentCompanyId,
          with_documents: true,
          page: this.page,
          ...params,
          _options: true,
        }
      )

      SFetch.json(url, { method: 'GET' }).then((response) => {
        this.permanentEntries = response.permanentEntries
        this.permanentCategory = response.permanentCategory
        this.page = response.currentPage
        this.totalPages = response.totalPages
        this.updateEntries()
        this.loading = false
      })
    },
    fetchDocuments(params = {}) {
      const url =
        Silverfin.Routes.period_specific_documents_permanent_entries_path({
          firm_id: Silverfin.currentFirmId,
          company_id: Silverfin.currentCompanyId,
          ...params,
          _options: true,
        })

      SFetch.json(url, { method: 'GET' }).then((response) => {
        this.documents = response
        this.updateEntries()
        this.loading = false
      })
    },
    async fetchExports(params = {}) {
      const url = Silverfin.Routes.all_in_ledger_exports_path({
        firm_id: Silverfin.currentFirmId,
        company_id: Silverfin.currentCompanyId,
        ledger_id: Silverfin.currentLedgerId(),
        ...params,
        _options: true,
      })
      try {
        const response = await SFetch.json(url, { method: 'GET' })
        this.exports = response.map((e) => ({ export: true, ...e }))
        this.updateEntries()
        this.loading = false
      } catch (e) {
        SfLog.warn('Failed to fetch exports', { extra: { error: e } })
      }
    },
  },
}
</script>
